/**
 * Precision CAP Portal — first-time MFA enrollment.
 *
 * Three plain steps:
 *   1. Install an authenticator app (no SMS — per policy).
 *   2. Scan the QR or copy the secret manually.
 *   3. Type the current 6-digit code to confirm it works.
 *
 * The enroll_token is one-shot and short-lived. We accept it from
 * location.state (passed by login/redeem) or from sessionStorage as a
 * fallback for page reloads.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';

const API = process.env.REACT_APP_API_URL || '';

const STEP_INSTALL = 'install';
const STEP_SCAN = 'scan';
const STEP_VERIFY = 'verify';

export default function PortalMfaEnroll() {
  const location = useLocation();
  const navigate = useNavigate();
  const codeRef = useRef(null);

  const enrollToken =
    location.state?.enroll_token ||
    sessionStorage.getItem('portal_enroll_token') ||
    '';

  const [step, setStep] = useState(STEP_INSTALL);
  const [enrollData, setEnrollData] = useState(null); // { secret, qr_data_url, otpauth_uri }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enrollToken) {
      navigate('/login');
    }
  }, [enrollToken, navigate]);

  useEffect(() => {
    if (step === STEP_VERIFY && codeRef.current) codeRef.current.focus();
  }, [step]);

  const startEnrollment = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/portal/mfa/enroll/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enroll_token: enrollToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'We could not start enrollment. Please sign in again.');
        setLoading(false);
        return;
      }
      setEnrollData(data);
      setStep(STEP_SCAN);
    } catch {
      setError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmed = code.trim().replace(/\s+/g, '');
      const res = await fetch(`${API}/api/portal/mfa/enroll/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          enroll_token: enrollToken,
          secret: enrollData?.secret,
          code: trimmed,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          data.message ||
            'That code did not match. Make sure your authenticator is showing the current code, then try again.',
        );
        setCode('');
        setLoading(false);
        return;
      }
      // Session is now active — clean up the one-shot token and proceed.
      sessionStorage.removeItem('portal_enroll_token');
      navigate('/cap');
    } catch {
      setError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-xl mx-auto">
        <h1 className="text-[28px] font-semibold text-slate-900 leading-tight">
          Turn on two-factor authentication
        </h1>
        <p className="text-slate-600 mt-2 text-[15px]">
          Your account requires a second factor at sign-in. This takes about two minutes and
          uses any standard authenticator app — no SMS.
        </p>

        <div className="mt-8 bg-white ring-1 ring-slate-200 rounded-xl p-6">
          {step === STEP_INSTALL && (
            <div className="space-y-5">
              <h2 className="text-[15px] font-semibold text-slate-900">
                Step 1 — install an authenticator app
              </h2>
              <p className="text-sm text-slate-600">
                If you don't already have one on your phone, install any of these. They are all
                free and take about thirty seconds.
              </p>
              <ul className="text-sm text-slate-700 space-y-1.5 ml-4 list-disc">
                <li><strong>Microsoft Authenticator</strong> — recommended if you use other Microsoft tools.</li>
                <li><strong>Google Authenticator</strong> — simplest, widely used.</li>
                <li><strong>1Password</strong> or <strong>Bitwarden</strong> — if you already use one as your password manager.</li>
                <li><strong>Authy</strong> — backs up codes across devices.</li>
              </ul>
              <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-3 text-sm text-amber-900">
                Please don't use SMS-based two-factor. Per policy, only authenticator apps and
                hardware security keys are accepted.
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={startEnrollment}
                disabled={loading}
                className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white transition ${
                  loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-800'
                }`}
              >
                {loading ? 'Loading…' : "I have an authenticator app — show me the QR code"}
              </button>
            </div>
          )}

          {step === STEP_SCAN && enrollData && (
            <div className="space-y-5">
              <h2 className="text-[15px] font-semibold text-slate-900">
                Step 2 — scan this QR code
              </h2>
              <p className="text-sm text-slate-600">
                Open your authenticator app, tap "Add account" or the "+" icon, and scan the
                code below.
              </p>

              <div className="flex justify-center">
                <div className="p-4 bg-white ring-1 ring-slate-200 rounded-xl">
                  <img
                    src={enrollData.qr_data_url}
                    alt="MFA QR code"
                    width={240}
                    height={240}
                  />
                </div>
              </div>

              {enrollData.secret && (
                <details className="text-sm text-slate-600">
                  <summary className="cursor-pointer hover:text-slate-900 font-medium">
                    Can't scan? Enter this code manually instead.
                  </summary>
                  <div className="mt-3 p-3 bg-slate-50 ring-1 ring-slate-200 rounded-md font-mono text-xs break-all select-all">
                    {enrollData.secret}
                  </div>
                </details>
              )}

              {error && (
                <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={() => setStep(STEP_VERIFY)}
                className="w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 transition"
              >
                Next — enter the 6-digit code
              </button>
            </div>
          )}

          {step === STEP_VERIFY && (
            <form onSubmit={verifyCode} className="space-y-5">
              <h2 className="text-[15px] font-semibold text-slate-900">
                Step 3 — confirm it's working
              </h2>
              <p className="text-sm text-slate-600">
                Type the current 6-digit code your authenticator is showing. The code rotates
                every 30 seconds — if it changes mid-typing, just enter the new one.
              </p>

              <div>
                <label htmlFor="portal-enroll-code" className="block text-sm font-medium text-slate-700 mb-1">
                  6-digit code
                </label>
                <input
                  ref={codeRef}
                  id="portal-enroll-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  className="w-full px-3 py-2.5 text-lg tracking-widest text-center font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(STEP_SCAN)}
                  className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium text-slate-700 bg-white ring-1 ring-slate-200 hover:bg-slate-50 transition"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || !code.trim()}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium text-white transition ${
                    loading || !code.trim()
                      ? 'bg-slate-400 cursor-not-allowed'
                      : 'bg-slate-900 hover:bg-slate-800'
                  }`}
                >
                  {loading ? 'Verifying…' : 'Confirm and continue'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
